import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import logoImg from "@assets/logo.png";
import type { Category } from "@shared/schema";

export function Footer() {
  const { data: categories } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
  });

  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/site/settings"],
    staleTime: 5 * 60 * 1000,
  });

  const disclaimer = siteSettings?.footer_disclaimer?.trim() || null;

  const templateCategories = categories?.filter((c) => c.group === "templates") || [];
  const indicatorCategories = categories?.filter((c) => c.group === "indicators") || [];

  return (
    <footer className="border-t bg-background/80 backdrop-blur-md" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src={logoImg} alt="Sierra Chart Pro" className="h-14 object-contain" />
              <div className="flex flex-col leading-tight">
                <span className="text-lg font-bold tracking-tight">
                  <span className="text-gray-400">Sierra</span><span className="text-gray-400">Chart</span><span className="text-primary">Pro</span><span className="text-gray-400">.com</span>
                </span>
                <span className="text-xs text-muted-foreground tracking-wide">Trading Templates &amp; Tools</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Premium Sierra Chart templates and indicators built by professional traders for serious market analysis.
            </p>
          </div>

          {templateCategories.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                Templates
              </h3>
              <ul className="space-y-2">
                {templateCategories.map((cat) => (
                  <li key={cat.id}>
                    <Link href={`/category/${cat.slug}`}>
                      <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid={`link-footer-category-${cat.id}`}>
                        {cat.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {indicatorCategories.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                Indicators
              </h3>
              <ul className="space-y-2">
                {indicatorCategories.map((cat) => (
                  <li key={cat.id}>
                    <Link href={`/category/${cat.slug}`}>
                      <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid={`link-footer-category-${cat.id}`}>
                        {cat.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
              Resources
            </h3>
            <ul className="space-y-2">
              <li>
                <Link href="/blog">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-blog">
                    Blog
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/tutorials">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-tutorials">
                    Tutorials
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/about">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-about">
                    About Us
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/testimonials">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-testimonials">
                    Testimonials
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
              Support
            </h3>
            <ul className="space-y-2">
              <li>
                <Link href="/faq">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-faq">
                    FAQ
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/terms">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-terms">
                    Terms of Service
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/contact">
                  <span className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="link-footer-contact">
                    Contact Us
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t space-y-3">
          {disclaimer && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {disclaimer}
            </p>
          )}
          <p className="text-xs text-muted-foreground text-center">
            &copy; {new Date().getFullYear()} Sierra Chart Pro. All rights reserved. Sierra Chart is a registered trademark.
          </p>
        </div>
      </div>
    </footer>
  );
}
